import React, { useCallback, useMemo } from 'react';
import { ClanName, Interpolate, MemberName } from '@wg/wows-react-uikit';
import equal from 'fast-deep-equal/react';
import get from 'lodash/get';
import moment from 'moment';

import DogTag from '~/Components/DogTag/DogTag';
import { Col, Container, Row } from '~/Components/Grid/Grid';
import InviteButton from '~/Components/InviteButton/InviteButton';
import ProfilePrivacy from '~/Components/ProfilePrivacy/ProfilePrivacy';
import SearchPanel from '~/Components/SearchPanel/SearchPanel';
import preloaded from '~/preloaded';
import { RootState, useAppDispatch, useAppSelector } from '~/store';
import { appActions } from '~/store/appSlice';
import { isInGame } from '~/utils';
import { removeActionsFromQueue } from '~/utils/externalNavigation';
import { t } from '~/utils/localization';
import { formatName } from '~/utils/players';
import { getRoles, permissions } from '~/utils/roles';

import styles from './ProfileHeader.scss';
import { UISticky } from '~/Components/UISticky/UISticky';
import classnames from 'classnames';

const isSettingsVisible = window.preloaded.is_visible_settings_show;

export interface ProfileHeaderProps {
  noSearch?: boolean;
}

const stateSelector = (state: RootState) => {
  return {
    ownClanRole: state.app.ownClanRole,
    hasOwnClan: state.app.hasOwnClan,
    authSpaId: state.app.authSpaId,
    spaId: state.app.spaId,
    account: state.app.account,
    clanData: state.app.clanData,
    isShipTableHeaderSticky: state.app.isShipTableHeaderSticky,
  };
};

const getRegistrationDate = (account: Account | null): string | null => {
  const created_at = account ? get(account, 'statistics.basic.created_at', account.created_at) : null;
  return created_at ? moment(created_at * 1000).format('DD.MM.YYYY') : null;
};

const ProfileHeader = (props: ProfileHeaderProps) => {
  const dispatch = useAppDispatch();

  const { noSearch } = props;
  const { account, clanData, isShipTableHeaderSticky, authSpaId, spaId, hasOwnClan, ownClanRole } = useAppSelector(
    stateSelector,
    equal,
  );

  const registrationDate = getRegistrationDate(account);
  const name = formatName(account ? account.name : '');
  const roleName = clanData ? clanData.role : ('' as RoleName);
  const color = get(clanData, 'clan.color', undefined);
  const hexColor = color ? `#${color.toString(16)}` : '#fff';
  const isHidden = account ? account.hidden_profile : false;
  const tag = get(clanData, 'clan.tag', undefined);
  const clanName = get(clanData, 'clan.name', undefined);
  const clanId = get(clanData, 'clan_id', undefined);

  const topOffset = isInGame ? 0.1 : 37;
  const isMyProfile = authSpaId && authSpaId === spaId;

  const roles = getRoles(t);
  const role = get(roles, roleName, undefined) as Role;
  const ownRole = get(roles, ownClanRole, undefined) as Role;

  const hasPermission = ownRole && ownRole.permissions.includes(permissions.INVITE);
  const hasClan = Boolean(tag && clanName);

  const clanUrl = useMemo(() => {
    if (!preloaded.settings.wsclansHost) {
      return null;
    }

    const returnUrl = encodeURIComponent(window.location.href);
    return `https://${preloaded.settings.wsclansHost}/clan-profile/${clanId}?returnUrl=${returnUrl}`;
  }, [clanId]);

  const handleOpenClanProfile = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.preventDefault();

      if (!clanUrl) {
        return;
      }

      if (isInGame) {
        dispatch(appActions.setRedirectFetching({ isFetching: true }));
        removeActionsFromQueue((action) => action.actionId === 'goToWebProject');
        window.location.href = clanUrl;
      } else {
        window.open(clanUrl, '_blank');
      }
    },
    [dispatch, clanUrl, isInGame],
  );
  return (
    <UISticky topOffset={topOffset}>
      {({ style, isSticky }) => {
        const stickyStyle: any = style;
        const wrapper = classnames(styles.wrapper, {
          [styles.stickyWrapper]: isSticky,
          [styles.stickyWrapperShadow]: isSticky && !isShipTableHeaderSticky,
        });
        const dogtag = classnames(styles.dogtag, {
          [styles.dogtagSmall]: isSticky,
        });

        return (
          <div
            style={{
              ...stickyStyle,
              zIndex: 900,
              top: isSticky ? `${topOffset}px` : undefined,
            }}
          >
            <Container className={wrapper} id={'profileHeader'}>
              <Row className={styles.rowWrapper}>
                <Col size={6}>
                  <div className={dogtag}>
                    {!isHidden ? <DogTag clanTag={tag} small={isSticky} /> : null}
                    <div className={styles.nameWrapper}>
                      <div className={styles.name}>{name}</div>
                      {!isHidden && registrationDate && (
                        <div className={styles.registration}>
                          <Interpolate str={t('Registration date: %(date)s')} date={registrationDate} />
                        </div>
                      )}
                      {account && account.visibility_settings && isSettingsVisible && (
                        <ProfilePrivacy
                          id={account.id}
                          visibilitySettings={account.visibility_settings}
                          isClanMember={clanId && clanName}
                        />
                      )}
                    </div>
                  </div>
                </Col>
                <Col size={noSearch ? 6 : 3}>
                  <div className={styles.clanData}>
                    {hasClan &&
                      (clanUrl ? (
                        <div className={styles.clanLink} onClick={handleOpenClanProfile}>
                          <ClanName clanColor={hexColor} clanName={clanName} clanTag={tag} />
                        </div>
                      ) : (
                        <ClanName clanColor={hexColor} clanName={clanName} clanTag={tag} />
                      ))}
                    {!clanId && !isMyProfile && hasOwnClan && hasPermission && <InviteButton />}
                    <MemberName isInline={false} name={role ? role.localized_name : ''} role={role ? role.name : ''} />
                  </div>
                </Col>
                {noSearch ? null : (
                  <Col size={3} className={styles.searchWrapper}>
                    <div className={styles.searchWrapperHeight}>
                      <SearchPanel />
                    </div>
                  </Col>
                )}
              </Row>
            </Container>
          </div>
        );
      }}
    </UISticky>
  );
};

export default ProfileHeader;
